import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { isCreditPopupSuppressed, resetCreditPopupSuppressions } from 'components/payments/CreditConfirmPopup';
import { store } from '../store';
import { apiService } from '../services/apiService';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CreditConfirmOptions {
  /** Unique key for this module – used to persist "never show again" */
  moduleKey: string;
  /** Human-readable module name shown in the popup */
  moduleName: string;
  /** Feature key to look up credit cost from the credits store */
  featureKey?: string;
  /** Override credit cost when featureKey lookup isn't available */
  creditCost?: number;
  /** Short description of what the action does */
  actionDescription?: string;
}

export interface UseCreditConfirmReturn {
  /** Whether the popup is currently open */
  isOpen: boolean;
  /** Current popup options */
  popupOptions: CreditConfirmOptions | null;
  /**
   * Call this before any credit-consuming action.
   * - If the module is suppressed → resolves immediately with `true`
   * - Otherwise → opens the popup and resolves with `true` (confirm) or `false` (cancel)
   */
  requestConfirm: (options: CreditConfirmOptions) => Promise<boolean>;
  /** Call from popup's onConfirm prop */
  handleConfirm: () => void;
  /** Call from popup's onCancel prop */
  handleCancel: () => void;
  /** Call from popup's onNeverShowAgain prop (popup handles suppression internally) */
  handleNeverShowAgain: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useCreditConfirm
 *
 * Manages the lifecycle of the CreditConfirmPopup.
 *
 * Usage:
 * ```tsx
 * const { isOpen, popupOptions, requestConfirm, handleConfirm, handleCancel, handleNeverShowAgain } = useCreditConfirm();
 *
 * const handleAction = async () => {
 *   const confirmed = await requestConfirm({
 *     moduleKey: 'candidate_profile_view',
 *     moduleName: 'Candidate Profile',
 *     featureKey: 'VIEW_PROFILE',
 *     actionDescription: 'Viewing this candidate profile will deduct credits.',
 *   });
 *   if (!confirmed) return;
 *   // proceed with the action
 * };
 *
 * // In JSX:
 * <CreditConfirmPopup
 *   isOpen={isOpen}
 *   {...popupOptions!}
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   onNeverShowAgain={handleNeverShowAgain}
 * />
 * ```
 */
export const useCreditConfirm = (): UseCreditConfirmReturn => {
  // One-time: clear stale suppressions written by builds that pre-checked
  // "Don't show again" (popup never appeared again after first Confirm).
  resetCreditPopupSuppressions();

  const [isOpen, setIsOpen] = useState(false);
  const [popupOptions, setPopupOptions] = useState<CreditConfirmOptions | null>(null);

  // Holds the resolve function of the pending Promise
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const requestConfirm = useCallback(
    async (options: CreditConfirmOptions): Promise<boolean> => {
      // If user has suppressed this module, verify balance upfront
      if (isCreditPopupSuppressed(options.moduleKey)) {
        if (options.creditCost && options.creditCost > 0) {
          const stateBal = store.getState().credits.balance;
          const bal = stateBal?.availableCredits ?? 0;
          if (bal < options.creditCost) {
            toast.error(`Insufficient credits. You need ${options.creditCost} credits for this action (current balance: ${bal}).`);
            return false;
          }
        }
        return Promise.resolve(true);
      }

      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        setPopupOptions(options);
        setIsOpen(true);
      });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  // "Never show again" – the popup itself calls suppressCreditPopup() then calls onConfirm
  // so we just resolve true here (popup already suppressed the module)
  const handleNeverShowAgain = useCallback(() => {
    setIsOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, []);

  return {
    isOpen,
    popupOptions,
    requestConfirm,
    handleConfirm,
    handleCancel,
    handleNeverShowAgain,
  };
};
